import React, { useContext, useEffect, useMemo } from 'react';

import { Box, Button, Card, CardContent, Grid, ListItem, Typography, useMediaQuery, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { signOut, useSession } from 'next-auth/react';
import { shape } from 'prop-types';

import phoneNumbers from '../../constants/phoneNumbers';
import { AccountOrdersContext } from '../../providers/AccountOrders';
import { standardTimeFormat } from '../../utils/datetime/formatTime';
import Link from '../Link';
import LoadingIndicator from '../shared/LoadingIndicator';
import { track } from '../../modules/segment/helpers';

const OrderListItem = ({ order }) => {
  const { update } = useSession();

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  const orderStatusText = useMemo(() => {
    switch (order.status) {
      case 'CREATED':
        return 'Pre-move';
      case 'COMPLETED':
      case 'CLOSED':
        return 'Completed';
      case 'CANCELLED':
        return 'Cancelled';
      default:
        return '';
    }
  }, [order.status]);

  return (
    <ListItem>
      <Card variant='outlined' sx={{ flexGrow: 1 }}>
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <Grid container alignItems='center' spacing={2}>
            <Grid item xs={4}>
              <Typography color='secondary' variant='h4'>
                {order.confirmation_id}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography whiteSpace='pre-wrap' textAlign='center'>
                {`Starting ${dayjs(order.start_date_time).tz(order.start_time_zone).format('MMM DD, YYYY')}${
                  mobile ? '\n' : ' '
                }@ ${standardTimeFormat({
                  datetime: dayjs(order.start_date_time).unix(),
                  zone: order.start_time_zone,
                  createdDate: dayjs(order.created_at).unix(),
                })}`}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography>{orderStatusText}</Typography>
            </Grid>
            <Grid item xs={8}>
              <Box mx={{ xs: 0, sm: 'auto' }} maxWidth={300}>
                <Button
                  variant='contained'
                  disabled={!(order.confirmation_id && order.zip)}
                  onClick={() => {
                    track('Dashboard - Account Orders - Select Order', { confirmationId: order.confirmation_id });
                    update({ reservationID: order.confirmation_id, reservationZIP: order.zip, orderId: order.id });
                  }}
                >
                  {!(order.confirmation_id && order.zip) ? `Not Available` : 'View'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </ListItem>
  );
};

OrderListItem.propTypes = {
  order: shape({}).isRequired,
};

const AccountOrders = () => {
  const { data: accountOrders, isFetching } = useContext(AccountOrdersContext);
  const { data: session, update } = useSession();

  const validAutoLogin = accountOrders?.length === 1 && accountOrders?.[0]?.status !== 'CANCELLED';

  useEffect(() => {
    if (accountOrders && !validAutoLogin) {
      if (accountOrders?.length > 1) {
        track('Dashboard Account Orders Page Viewed - Multiple Orders');
      } else if (accountOrders?.length === 0) {
        track('Dashboard Account Orders Page Viewed - No Orders');
      }
    }
    if (validAutoLogin && accountOrders?.[0]) {
      track('Dashboard Account Orders Page Viewed - Single Order - Auto Login');
      update({
        reservationID: accountOrders[0].confirmation_id,
        reservationZIP: accountOrders[0].zip,
        orderId: accountOrders[0].id,
      });
    }
  }, [accountOrders, validAutoLogin]);

  if (isFetching || validAutoLogin) {
    return <LoadingIndicator message='Loading Orders' loadingReason='Loading Orders' />;
  }

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 'calc(100vh - 100px)',
        backgroundImage: 'linear-gradient(360deg, rgba(155, 184, 248, 0.25), 0%, rgba(205, 219, 251, 0.25) 100%)',
      }}
    >
      <Box pt={5}>
        <Typography variant='h3' textAlign='center' mb={2}>
          Welcome!
        </Typography>
        {session?.user?.email ? (
          <Typography variant='h5' textAlign='center'>
            Logged in as: {session?.user?.email}
          </Typography>
        ) : null}
      </Box>
      <Box mt={2}>
        <Box maxWidth={400} mx='auto'>
          {accountOrders?.length ? (
            <Typography textAlign='center'>
              If an order is unavailable or missing from the list below, please give our support a call at{' '}
              <Link href={`tel:${phoneNumbers.CUSTOMER_SERVICE}`} external>
                {phoneNumbers.CUSTOMER_SERVICE}
              </Link>
            </Typography>
          ) : (
            <Box mt={2} maxWidth={290} mx='auto'>
              <Typography textAlign='center' variant='h4' pt={3} pb={5}>
                No Orders Found
              </Typography>
              <Typography textAlign='center'>
                Please call our support at{' '}
                <Link href={`tel:${phoneNumbers.CUSTOMER_SERVICE}`} external>
                  {phoneNumbers.CUSTOMER_SERVICE}
                </Link>{' '}
                if you have a reservation with us.
              </Typography>
            </Box>
          )}
        </Box>
        {accountOrders && typeof accountOrders === 'object' ? (
          <Box mt={2} maxWidth={800} mx='auto'>
            {accountOrders?.map((order) => (
              <OrderListItem order={order} />
            ))}
          </Box>
        ) : null}
        <Box maxWidth={400} mx='auto' px={4} pt={4}>
          <Button
            variant='contained'
            onClick={() => {
              track('Dashboard - Account Orders - Logout');
              signOut({ callbackUrl: '/logout' });
            }}
          >
            Log out
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AccountOrders;
